// NOTE: Required packages - install with: npm install d3-sankey d3-shape d3-selection
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { select } from "d3-selection";
import { sankey, sankeyLinkHorizontal, sankeyLeft } from "d3-sankey";
import { getMassBalance47Sankey } from "../../../../../../apis/Product/MassBalanceFlow47Api";
import "./index.css";

const W = 1600;
const H = 700;

const MassBalanceSankey = ({ productId }) => {
  const { t } = useTranslation();
  const [tooltip, setTooltip] = useState(null);
  const [hoveredLink, setHoveredLink] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [sankeyData, setSankeyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const svgRef = useRef(null);

  // Fetch Sankey data from API
  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    getMassBalance47Sankey({
      productId,
      setObjects: (data) => {
        setSankeyData(data);
        setLoading(false);
      },
    });
  }, [productId]);

  // Process and render Sankey diagram
  useEffect(() => {
    if (!sankeyData || !sankeyData.nodes || !sankeyData.links || !svgRef.current) return;

    const svg = select(svgRef.current);
    svg.selectAll("*").remove(); // Clear previous render

    const { nodes: rawNodes, links: rawLinks } = sankeyData;

    // Prepare nodes for d3-sankey (add name property)
    const sankeyNodes = rawNodes.map((node, index) => ({
      ...node,
      name: node.label || node.node_id,
      index: index,
    }));

    // Prepare links - backend provides numeric indices
    const sankeyLinks = rawLinks.map((link) => ({
      source: link.source, // Already numeric index from backend
      target: link.target, // Already numeric index from backend
      value: parseFloat(link.value) || 0,
      color: link.color,
    }));

    // Create Sankey generator
    const sankeyGenerator = sankey()
      .nodeId((d) => d.index)
      .nodeWidth(60)
      .nodePadding(8)
      .nodeAlign(sankeyLeft)
      .extent([[20, 20], [W - 20, H - 20]]);

    // Generate layout
    const result = sankeyGenerator({
      nodes: sankeyNodes,
      links: sankeyLinks,
    });

    const layoutNodes = result.nodes;
    const layoutLinks = result.links;

    // Create link path generator
    const linkPath = sankeyLinkHorizontal();

    // Render links
    const linkSelection = svg
      .append("g")
      .attr("class", "sankey-links")
      .selectAll(".link")
      .data(layoutLinks)
      .enter()
      .append("path")
      .attr("class", "link")
      .attr("d", linkPath)
      .attr("stroke", (d) => {
        // Use link color if provided, otherwise use source node color
        return d.color || (layoutNodes[d.source.index]?.color || "#999");
      })
      .attr("stroke-width", (d) => Math.max(1, d.width || 1))
      .attr("fill", "none")
      .attr("opacity", (d) => {
        const isHov =
          hoveredLink === d ||
          hoveredNode === d.source.index ||
          hoveredNode === d.target.index;
        const dimmed = (hoveredLink !== null || hoveredNode !== null) && !isHov;
        return dimmed ? 0.08 : isHov ? 0.75 : 0.42;
      })
      .style("cursor", "pointer")
      .on("mouseenter", function (event, d) {
        setHoveredLink(d);
        const sourceNode = layoutNodes[d.source.index];
        const targetNode = layoutNodes[d.target.index];
        setTooltip({
          x: event.clientX,
          y: event.clientY,
          text: `${sourceNode?.label || "Source"} → ${targetNode?.label || "Target"}: ${d.value?.toFixed(2) || 0} kg`,
        });
      })
      .on("mouseleave", function () {
        setHoveredLink(null);
        setTooltip(null);
      });

    // Render nodes
    const nodeGroup = svg
      .append("g")
      .attr("class", "sankey-nodes")
      .selectAll(".node")
      .data(layoutNodes)
      .enter()
      .append("g")
      .attr("class", "node")
      .attr("transform", (d) => `translate(${d.x0},${d.y0})`)
      .style("cursor", "pointer")
      .on("mouseenter", function (event, d) {
        setHoveredNode(d.index);
        const totalValue = layoutLinks
          .filter((l) => l.source.index === d.index || l.target.index === d.index)
          .reduce((sum, l) => sum + (l.value || 0), 0);
        setTooltip({
          x: event.clientX,
          y: event.clientY,
          text: `${d.label || d.node_id}: ${totalValue.toFixed(2)} kg`,
        });
      })
      .on("mouseleave", function () {
        setHoveredNode(null);
        setTooltip(null);
      });

    // Node rectangles
    nodeGroup
      .append("rect")
      .attr("height", (d) => Math.max(4, d.y1 - d.y0))
      .attr("width", (d) => d.x1 - d.x0)
      .attr("fill", (d) => d.color || "#999")
      .attr("rx", 3)
      .attr("opacity", (d) => {
        const dimmed = hoveredNode !== null && hoveredNode !== d.index;
        return dimmed ? 0.25 : 1;
      });

    // Node labels
    nodeGroup.each(function (d) {
      const node = select(this);
      const w = d.x1 - d.x0;
      const h = d.y1 - d.y0;
      const isLeft = d.x0 < W * 0.5;
      const showInlineLabel = !d.isPhase && h >= 18;
      const showKg = !d.isPhase && h >= 30;

      if (d.isPhase) {
        // Phase nodes: center text
        node
          .append("text")
          .attr("x", w / 2)
          .attr("y", h / 2)
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "middle")
          .attr("font-size", h > 40 ? 16 : 12)
          .attr("font-weight", "800")
          .attr("fill", "white")
          .text(d.label);
      } else if (showInlineLabel) {
        // Inline label for larger nodes
        node
          .append("text")
          .attr("x", w / 2)
          .attr("y", h / 2 - (showKg ? 7 : 0))
          .attr("text-anchor", "middle")
          .attr("dominant-baseline", "middle")
          .attr("font-size", Math.min(13, Math.max(9, h * 0.32)))
          .attr("font-weight", "700")
          .attr("fill", "white")
          .attr("opacity", hoveredNode !== null && hoveredNode !== d.index ? 0.3 : 1)
          .text(d.label);

        if (showKg) {
          const totalValue = layoutLinks
            .filter((l) => l.source.index === d.index || l.target.index === d.index)
            .reduce((sum, l) => sum + (l.value || 0), 0);
          node
            .append("text")
            .attr("x", w / 2)
            .attr("y", h / 2 + 9)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("font-size", Math.min(11, Math.max(8, h * 0.24)))
            .attr("fill", "rgba(255,255,255,0.85)")
            .attr("opacity", hoveredNode !== null && hoveredNode !== d.index ? 0.3 : 1)
            .text(`${totalValue.toFixed(1)} kg`);
        }
      } else {
        // External label for smaller nodes
        node
          .append("text")
          .attr("x", isLeft ? -5 : w + 5)
          .attr("y", h / 2)
          .attr("text-anchor", isLeft ? "end" : "start")
          .attr("dominant-baseline", "middle")
          .attr("font-size", 9)
          .attr("fill", "hsl(var(--foreground))")
          .attr("opacity", hoveredNode !== null && hoveredNode !== d.index ? 0.3 : 0.85)
          .text(d.label);
      }
    });
  }, [sankeyData, hoveredLink, hoveredNode]);

  // Color constants for legend
  const C_BLUE = "#7ab8f5";
  const C_SALMON = "#e8907a";
  const C_GOLD = "#f0c96a";
  const C_GRAY = "#b0b8c4";

  if (loading) {
    return (
      <div className="mass-balance-sankey-container">
        <div className="mass-balance-sankey-loading">
          {t("LOADING") || "Loading Sankey diagram..."}
        </div>
      </div>
    );
  }

  if (!sankeyData || !sankeyData.nodes || sankeyData.nodes.length === 0) {
    return (
      <div className="mass-balance-sankey-container">
        <div className="mass-balance-sankey-placeholder">
          <p className="mass-balance-sankey-placeholder-text">
            {t("NO_SANKEY_DATA") || "No Sankey diagram data available. Please complete the LCA flows first."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mass-balance-sankey-container">
      {/* Legend */}
      <div className="mass-balance-sankey-legend">
        {[
          { color: C_BLUE, label: t("MATERIAL_WATER_INPUTS") || "Material / water inputs" },
          { color: C_GOLD, label: t("PRODUCT_FORWARD_FLOW") || "Product forward flow" },
          { color: C_SALMON, label: t("WASTE_SURPLUS_LOSSES") || "Waste / surplus / losses" },
          { color: C_GRAY, label: t("EQUIPMENT_ENERGY") || "Equipment / energy" },
        ].map(({ color, label }) => (
          <span key={label} className="mass-balance-legend-item">
            <span className="mass-balance-legend-color" style={{ background: color }}></span>
            {label}
          </span>
        ))}
      </div>

      {/* SVG Container */}
      <div className="mass-balance-sankey-svg-wrapper">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className="mass-balance-sankey-svg"
          style={{ minHeight: 400, background: "transparent" }}
        />
      </div>

      {/* Floating tooltip */}
      {tooltip && (
        <div
          className="mass-balance-sankey-tooltip"
          style={{ left: tooltip.x + 12, top: tooltip.y - 32 }}
        >
          {tooltip.text}
        </div>
      )}
    </div>
  );
};

export default MassBalanceSankey;
